import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import bowser from 'yola-bowser';
import { dialogs, i18next, site } from '@yola/ws-sdk';
import { DifmOrderForm, utils as webappPluginsUtils } from '@yola/webapp-plugins';
import { selectors, verifiers } from '@yola/subscription-manager-js';
import yousers from '@yola/yousersjs';
import config from 'src/js/modules/config';
import { saveSiteWithEffect } from 'src/js/modules/saving/helpers/save-site';
import segment from 'src/js/modules/analytics/segment';
import getFieldValuesFromSettings from 'src/js/modules/website-settings/helpers/get-field-values-from-settings';
import utils from 'yola-editor/src/js/router/utils';
import yolaEditorDialogs from 'yola-editor/src/js/modules/dialogs';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';
import { snakeCaseObjectToCamelCase } from 'yola-editor/src/js/utils/convert-case';
import yolaSite from 'yola-editor/src/js/modules/site';
import user from 'yola-editor/src/js/modules/user';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import { hasGoldSubscriptionWithDifmPromo } from '../verifiers/has-gold-subscription';

const {
  constants: { events },
  trackAsync,
  trackers: { trackEvent },
} = segment;
const { generatePaymentUrl } = webappPluginsUtils;

const DifmOrderFormDialogContainer = () => {
  const siteSettings = useSelector(site.selectors.getSettings);
  const domainName = useSelector(yolaSite.selectors.getSiteURL);
  const activeHostingSubscription = useSelector(selectors.getActiveHostingSubscription);
  const userData = useSelector(user.selectors.getUserData);
  const calendlyDifmPromoOnboardingUrl = useSelector(
    config.selectors.getCalendlyDifmPromoOnboardingUrl
  );
  const subscriptionsList = useSelector(selectors.getSubscriptionList);
  const subscriptionsTypes = subscriptionsList.map(({ type }) => type);
  const hasEverHadPaidHostingSubscription = verifiers.hasPaidHostingPackageType(subscriptionsTypes);

  const [featureFlags] = useFeatureFlags(['free_trial_upsell']);

  const { free_trial_upsell: isFreeTrialUpsellFeatureFlagEnabled } = featureFlags;

  const isMobile = bowser.mobile;
  const isSafari = bowser.safari;
  const { name, surname, email: userEmail } = userData;
  const userFullName = `${name} ${surname}`;
  const businessCategory = site.accessors.getBusinessCategory();
  const isPromoFlow = hasGoldSubscriptionWithDifmPromo();
  const isFreeTrialAvailable =
    isFreeTrialUpsellFeatureFlagEnabled && !hasEverHadPaidHostingSubscription;

  const {
    emails: businessEmail,
    phones: businessPhone,
    addresses: businessAddress,
    businessHours,
    businessName: businessNameSetting,
    siteName,
  } = getFieldValuesFromSettings(siteSettings);
  const businessName = businessNameSetting || siteName;
  const details = Object.fromEntries(
    Object.entries({
      domainName,
      businessEmail,
      businessPhone,
      businessAddress,
      businessHours,
      businessName,
    }).filter(([, value]) => Boolean(value))
  );

  const dispatch = useDispatch();

  const onBack = ({ stepId }) => {
    trackEvent(events.DIFM_ORDER_FORM_BACK_STEP_CLICKED, {
      stepId,
      businessCategory,
    });
  };

  const onNext = ({ stepId }) => {
    trackEvent(events.DIFM_ORDER_FORM_NEXT_STEP_CLICKED, {
      stepId,
      businessCategory,
    });
  };

  const onChange = ({ stepId, targetStepId }) => {
    trackEvent(events.DIFM_ORDER_FORM_GOTO_STEP_CLICKED, {
      stepId,
      targetStepId,
      businessCategory,
    });
  };

  const onClose = ({ stepId }) => {
    trackEvent(events.DIFM_ORDER_FORM_CLOSE_CLICKED, {
      stepId,
      businessCategory,
    });
    dialogs.operations.hide();
  };

  const onDisplay = ({ stepId }) => {
    trackEvent(events.DIFM_ORDER_FORM_DISPLAYED, {
      stepId,
      businessCategory,
    });
  };

  const onSubmit = async ({ fieldData, traitsData, paymentUrl, paymentData }) => {
    dispatch(
      yolaEditorDialogs.actions.show(yolaEditorDialogs.dialogTypes.PENDING, {
        captions: {
          title: i18next.t('Loading...'),
        },
      })
    );

    await trackAsync(events.DIFM_ORDER_FORM_COMPLETED, snakeCaseObjectToCamelCase(traitsData));

    if (isPromoFlow) {
      try {
        await yousers.requests.submitDifmPromoOrder(fieldData);

        saveSiteWithEffect(() => {
          if (isSafari) {
            // Safari's popup blocker prevents programmatic link clicks in async contexts
            // So we redirect instead of opening new tab as a fallback
            utils.redirectToUrl(calendlyDifmPromoOnboardingUrl);
          } else {
            utils.openInNewTab(calendlyDifmPromoOnboardingUrl);
            utils.reload();
          }
        });
      } catch (error) {
        console.error(error);
        yolaEditorDialogs.operations.hide();
        dialogs.operations.show(dialogTypes.DIFM_ERROR_DIALOG, {
          captions: {
            title: i18next.t('Couldn’t send email'),
            description: i18next.t(
              'For an unknown reason, your email could not be sent. Please try again later.'
            ),
            dismiss: i18next.t('Return'),
          },
          onDismiss: () => {
            dialogs.operations.hide();
          },
        });
      }

      return;
    }

    const billingUrl = isFreeTrialAvailable
      ? generatePaymentUrl({ ...paymentData, trial_package: '1' })
      : paymentUrl;

    saveSiteWithEffect(() => {
      utils.redirectToUrl(billingUrl);
    });
  };

  return (
    <DifmOrderForm
      initialValues={{
        userFullName,
        userEmail,
      }}
      details={details}
      term={activeHostingSubscription?.term}
      isMobile={isMobile}
      isPromoFlow={isPromoFlow}
      onBack={onBack}
      onNext={onNext}
      onChange={onChange}
      onClose={onClose}
      onDisplay={onDisplay}
      onSubmit={onSubmit}
    />
  );
};

export default DifmOrderFormDialogContainer;
